import * as THREE from "three";
import { MATS, useEnvironment } from "@catworld/engine";
import { DAY_LENGTH_S, dayPhase } from "@catworld/shared";

/**
 * The day/night cycle: sky, sun, moon, and every ambient term, driven off
 * one number -- the phase of the day in [0, 1), midnight to midnight.
 *
 * WHO OWNS THE CLOCK.  The server does, exactly as it owns the prey: two
 * people standing on the same lawn must see the same sunset, so the time of
 * day is world state and this module renders whatever the server says it is
 * (the `day` message, about once a second).  Between words the phase is
 * integrated locally with dt, and the residual error is EASED out rather
 * than assigned -- the same argument net/client.js makes about the render
 * clock: a hard assignment once a second would make the sun tick like a
 * second hand.  Before the first word (and forever, offline) the phase
 * comes from the client's own wall clock through the very same dayPhase
 * formula the server uses, so the first frame is already right up to clock
 * skew and the artifact build -- which has no server at all -- still gets
 * its days and nights.  Why wall clock and not uptime is dayPhase's own
 * comment in shared/constants.js; it is the one decision this whole feature
 * hangs on.
 *
 * WHAT NEVER CHANGES.  scene.environmentIntensity stays 0.32 and the
 * hemisphere stays 0.85, all day and all night.  That split was SOLVED, not
 * eyeballed -- against the pre-environment lighting it lands sky-facing
 * irradiance at 1.00x (the hemisphere comment below) -- and multiplying one
 * term through the cycle and not the other would quietly bend it.  So the
 * cycle lives entirely in COLOURS: each keyframe's hemisphere sky tracks
 * the tint of its environment gradient's upper half and its hemisphere
 * ground tracks the dulled ground band, so when night darkens the pixels
 * of one it darkens the other and the solved ratio survives the hours.
 * One stated bend: through the day the two track proportionally, but at
 * night the hemisphere keyframes sit deliberately BRIGHTER than strict
 * proportion to the env pixels -- that is the "err well toward a readable
 * night" call, and taking it through the hemisphere rather than the moon
 * or the env map is a choice: extra hemisphere flattens contrast (fill
 * everywhere, so a kid can always see the cat) where extra key would
 * deepen the very shadows that hide it, and extra env would put a glow on
 * every roof.  (Between coarse environment rebuilds the hemisphere also
 * leads by at most one rebuild step -- ENV_STEP, about five seconds of
 * colour -- which is the size of error the ease already hides.)  The sun
 * is the exception and the point: it is a key light, outside the ambient
 * bargain, and its intensity is what a sunrise IS.
 */

// ---------------------------------------------------------------------------
// The gradients.
// ---------------------------------------------------------------------------

/**
 * Stop POSITIONS for the two gradients, fixed for every hour; the keyframes
 * below recolour the stops but never move them.
 *
 * The two tables differ deliberately, and the difference must survive the
 * cycle.  SKY is the backdrop: its lower half is never seen -- the level's
 * terrain covers all of it -- so it can stay bright and atmospheric.
 * ENVIRONMENT is what surfaces gather light from: its lower half is the
 * entire lower hemisphere every downward-facing surface sees, so it gets a
 * horizon at the halfway mark and a dulled lawn-and-asphalt ground below
 * it, dimming straight down.  Fed the backdrop instead, soffits and cats'
 * bellies were lit from a bright cream floor at about 1.9x what the
 * hemisphere's olive ground was giving them -- and a moonlit version of the
 * same mistake would glow the undersides of everything at midnight.  Every
 * keyframe below keeps its env ground band darker and duller than its sky,
 * day and night alike.  Only the upper halves match -- which is also all a
 * creek can reflect.
 */
const STOP_SKY = [0.00, 0.48, 0.72, 1.00];
const STOP_ENV = [0.00, 0.48, 0.50, 0.62, 1.00];

/**
 * The hours of the day, as colour keyframes.  Linear-interpolated in sRGB
 * (cyclically -- night at 0.87 wraps into midnight at 0.00), which for a
 * stylised sky is indistinguishable from doing it properly in linear light
 * and keeps the canvas-gradient stops and the light colours in one space.
 *
 * The 0.625 keyframe is the shipped art, VERBATIM: sky, environment, sun
 * colour 0xffe2b4 at 2.5, hemisphere 0xc6dcf0 over 0x74854f, bounce
 * 0xdccdb0, fog 0xc9d3d8 -- the late-afternoon look every other lighting
 * number in this file was tuned against.  Anchoring a keyframe to it means
 * that at that hour the street renders exactly as it did before this
 * feature existed, which is what made the rest of the table tunable at all:
 * every other hour is judged as a departure from a known-good frame.
 *
 * Night is bright on purpose.  This is a children's picture-book street,
 * and a kid has to see their cat: the midnight hemisphere runs at roughly a
 * third of the day's luminance with a cool moon key at 1.15 against the
 * sun's 2.5, which reads as "night" through colour temperature and the
 * navy sky far more than through darkness.  Real moonlight is five orders
 * of magnitude dimmer than this; realism was never the brief.
 *
 * `water` is a multiplier on the creek's own envMapIntensity, 1.0 by day.
 * The creek reads almost entirely off what it reflects, and at night the
 * environment's pixels dim to a third -- correct, but pushed far enough the
 * creek stops reading as water and becomes a hole in the grass.  The night
 * keyframes hand it back about half of what the darkness took, so it
 * settles at ~60% of its daytime shine: visibly darker, still visibly a
 * moonlit surface.  A multiplier on the AUTHORED value rather than a value
 * of its own, deliberately -- the water material is being retuned in a
 * parallel branch, and this module must ride whatever number materials.js
 * ships rather than quietly overwrite it (see _waterBase below).
 */
const KEYS = [
  { ph: 0.000,  // midnight -- deep navy, one bright moonlit band at the zenith
    sky: ["#1b2b4e", "#31456f", "#48597e", "#3c4a68"],
    env: ["#1b2b4e", "#31456f", "#3d4c6c", "#262c20", "#1e231b"],
    key: ["#b4cbee", 1.25], hemi: ["#6a82ae", "#444e3a"],
    bounce: "#4c586e", fog: "#333f58", water: 1.55 },
  { ph: 0.185,  // pre-dawn -- the navy lifts, nothing warm yet
    sky: ["#223259", "#3a4d78", "#5d6488", "#4d5573"],
    env: ["#223259", "#3a4d78", "#475677", "#292f22", "#21261c"],
    key: ["#b0c8ec", 1.10], hemi: ["#7086b0", "#47513c"],
    bounce: "#505c72", fog: "#3a4660", water: 1.45 },
  { ph: 0.248,  // sunrise -- pink-gold horizon, long cool shadows
    sky: ["#5f7fae", "#c0a3a4", "#eec18e", "#d9b489"],
    env: ["#5f7fae", "#c0a3a4", "#bd9f97", "#575340", "#4a4839"],
    key: ["#ffb87e", 1.70], hemi: ["#a9b5d3", "#636e49"],
    bounce: "#c2a68f", fog: "#c0b0a8", water: 1.10 },
  { ph: 0.330,  // morning -- crisp and a touch cooler than the anchor
    sky: ["#79a9d0", "#bcd4e0", "#dcd8c6", "#c8c3ac"],
    env: ["#79a9d0", "#bcd4e0", "#bed2da", "#676c54", "#5c6150"],
    key: ["#ffedc8", 2.30], hemi: ["#c8def0", "#75864f"],
    bounce: "#d8cdb4", fog: "#c9d4da", water: 1.00 },
  { ph: 0.500,  // noon -- brightest and whitest, shadows at their shortest
    sky: ["#6ca2d6", "#b6d2e4", "#e3ded2", "#cfc9b4"],
    env: ["#6ca2d6", "#b6d2e4", "#bed4de", "#6d7258", "#616653"],
    key: ["#fff3dc", 2.60], hemi: ["#cfe2f4", "#7a8b52"],
    bounce: "#ded4bd", fog: "#ccd7dd", water: 1.00 },
  { ph: 0.625,  // late afternoon -- THE SHIPPED ART, verbatim; see above
    sky: ["#7fa8c9", "#b9cfdc", "#dfd6bd", "#c9c0a6"],
    env: ["#7fa8c9", "#b9cfdc", "#bccfd8", "#686d55", "#5d6250"],
    key: ["#ffe2b4", 2.50], hemi: ["#c6dcf0", "#74854f"],
    bounce: "#dccdb0", fog: "#c9d3d8", water: 1.00 },
  { ph: 0.775,  // golden hour -- the warm low sun, the longest shadows
    sky: ["#66799f", "#c497a0", "#f2ab6d", "#dfa56d"],
    env: ["#66799f", "#c497a0", "#bd9484", "#5c5940", "#4e4e3b"],
    key: ["#ffa254", 2.20], hemi: ["#aeadc6", "#6b724a"],
    bounce: "#cfa385", fog: "#bfa8a2", water: 1.05 },
  { ph: 0.815,  // dusk -- blue hour, the last violet in the west, moon rising
    sky: ["#33406f", "#635d8c", "#a06e7e", "#856a72"],
    env: ["#33406f", "#635d8c", "#6a6386", "#383d31", "#2e332a"],
    key: ["#9fb4d8", 0.80], hemi: ["#7681a8", "#4f5644"],
    bounce: "#6a6a80", fog: "#5c5f7c", water: 1.30 },
  { ph: 0.870,  // night -- almost midnight, a shade warmer in the west
    sky: ["#203052", "#364a72", "#545c7e", "#454e6a"],
    env: ["#203052", "#364a72", "#41506e", "#282e22", "#20251c"],
    key: ["#b0c6ea", 1.15], hemi: ["#6c84af", "#454e3a"],
    bounce: "#4e5a70", fog: "#364258", water: 1.50 },
];

// ---------------------------------------------------------------------------
// The sun's road (and the moon's).
// ---------------------------------------------------------------------------

/** When the sun is up.  Day is 56% of the cycle and night 44% -- weighted
    toward daylight because that is where the game is easiest to read, while
    keeping night long enough (~9 real minutes) to be a place, not a blink. */
const SUNRISE = 0.23;
const SUNSET = 0.79;

/**
 * The path is SOLVED so that at the 0.625 anchor keyframe the sun sits at
 * exactly (9, 14, 6) relative to the player -- the shipped art's key, whose
 * shadow every fence and porch in the level was checked against.  Given the
 * anchor's day-fraction u = (0.625 - SUNRISE) / (SUNSET - SUNRISE), the
 * noon peak elevation and the azimuth sweep's origin fall out of the two
 * equations el(u) = EL_MAX * sin(PI*u) and az(u) = AZ0 + AZ_SWEEP * u; the
 * only free choice left is the sweep itself, 170 degrees, a whole day's
 * worth of shadow swing without ever pointing the key straight down the
 * fog.  Computed here rather than pasted so the anchor can never drift
 * from the constants it was derived from.  (EL_MAX lands at 65.5 degrees,
 * so noon shadows are short but never gone.)
 */
const KEY_ANCHOR = new THREE.Vector3(9, 14, 6);
const KEY_DIST = KEY_ANCHOR.length();
const AZ_SWEEP = THREE.MathUtils.degToRad(170);
const _uAnchor = (0.625 - SUNRISE) / (SUNSET - SUNRISE);
const SUN_EL_MAX = Math.atan2(KEY_ANCHOR.y, Math.hypot(KEY_ANCHOR.x, KEY_ANCHOR.z))
  / Math.sin(Math.PI * _uAnchor);
const AZ0 = Math.atan2(KEY_ANCHOR.x, KEY_ANCHOR.z) - AZ_SWEEP * _uAnchor;

/** The moon flies lower than the sun's 65.5 -- night shadows stay long --
    and walks the same road (same AZ0, same sweep), which is astronomical
    nonsense and visual sense: shadow direction keeps moving all night, and
    nobody in this game is navigating by the ecliptic. */
const MOON_EL_MAX = THREE.MathUtils.degToRad(55);

/** Below 8 degrees the key light's RENDERED elevation is clamped: a truly
    horizontal directional light drives the 26 m shadow camera through the
    level edge-on, and shadow acne at grazing incidence eats the bias
    budget.  The light's brightness at those phases is already fading to
    nothing (HORIZON_FADE), so the clamp is never visible -- the sun "sets"
    by dimming out at 8 degrees, not by touching the horizon. */
const EL_MIN = THREE.MathUtils.degToRad(8);

/** The rise/set crossfade: intensity scales with smoothstep(el, 0, 4deg),
    so the key is at zero strength at the exact SUNRISE/SUNSET joints where
    the light teleports from the sun's road to the moon's.  4 degrees is
    deliberately narrow -- about 40 real seconds -- so a sunrise still comes
    up hot off the horizon rather than fading in over half the morning. */
const HORIZON_FADE_DEG = 4;

// ---------------------------------------------------------------------------
// Update budgets.
// ---------------------------------------------------------------------------

/**
 * What moves per frame, and what moves on a coarse schedule.
 *
 * Per frame: the key light's direction, colour and intensity, hemisphere
 * and bounce colours, fog colour, the water multiplier -- a few dozen
 * lerps and uniform writes, immeasurable.
 *
 * ENV_STEP: the prefiltered environment is rebuilt when the phase has
 * moved 1/256 of a day (~4.7 real seconds).  Rebuilding means redrawing a
 * 512-px gradient strip and running PMREMGenerator over it -- measured in
 * a headless Chromium on this machine at 0.2-0.9 ms per rebuild (the 0.9
 * is the first call), which is the dominant cost in this file and still
 * comfortably inside one 60 Hz frame's spare change.  It is NOT run per
 * frame anyway, on principle: re-filtering a sky that moved 0.005% of its
 * range 60 times a second is work with no pixels to show for it, and on
 * an integrated phone GPU the same call will not be sub-millisecond.  At
 * 1/256 the fastest colour swing in the table (dusk, ~0.06 of a day end
 * to end) still gets ~15 rebuild steps, each under 7% of the swing,
 * applied at intensity 0.32 to mostly rough surfaces: invisible, and
 * verified so by eye at dusk.  The last cost is exposed as `envCostMs`
 * for the harness.
 *
 * BG_STEP: the background strip redraws at 1/2048 of a day (~0.6 s).  It
 * is only a 4x256 canvas and an upload -- nearly free -- but it is also
 * the most LOOKED-AT surface in the game, so it steps eight times as often
 * as the environment; at dusk that is >100 steps across the swing, each
 * under 1% -- below what the eye can pick off a gradient.
 */
const ENV_STEP = 1 / 256;
const BG_STEP = 1 / 2048;

/** Corrections from the server bigger than this snap instead of easing:
    0.02 of a day is 24 real seconds, far beyond what the 1 Hz cadence and
    a healthy clock can accumulate, so hitting it means the local guess was
    genuinely wrong (a tab clock minutes off, a laptop back from sleep) and
    swimming the sky across the gap would be a timelapse nobody asked for. */
const PHASE_SNAP = 0.02;
/** Ease constant for sub-snap corrections, seconds.  At 1 Hz words the
    residual is microseconds of day; 10 s spreads even a rare full-second
    correction below anything a shadow edge can show. */
const PHASE_TAU = 10;

// ---------------------------------------------------------------------------

const hx = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16),
                   parseInt(h.slice(5, 7), 16)];
/** Keyframes with colours pre-parsed to [r,g,b] once, at module load, so
    the per-frame mix never touches a string. */
const PARSED = KEYS.map((k) => ({
  ph: k.ph,
  sky: k.sky.map(hx), env: k.env.map(hx),
  key: hx(k.key[0]), keyI: k.key[1],
  hemiSky: hx(k.hemi[0]), hemiGround: hx(k.hemi[1]),
  bounce: hx(k.bounce), fog: hx(k.fog), water: k.water,
}));

/** Signed shortest distance from phase a to b on the day circle, in
    (-0.5, 0.5] -- the day's shortestArc. */
function wrapSigned(d) {
  d %= 1;
  if (d > 0.5) d -= 1;
  else if (d <= -0.5) d += 1;
  return d;
}

const lerp = (a, b, f) => a + (b - a) * f;
const lerp3 = (out, a, b, f) => {
  out[0] = lerp(a[0], b[0], f);
  out[1] = lerp(a[1], b[1], f);
  out[2] = lerp(a[2], b[2], f);
};
const css = (c) => `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;
const setCol = (col, c) =>
  col.setRGB(c[0] / 255, c[1] / 255, c[2] / 255, THREE.SRGBColorSpace);

export class DayCycle {
  /**
   * Builds and owns everything in the scene that the time of day touches:
   * the background, the environment map, the fog, the key light (sun by
   * day, moon by night -- ONE DirectionalLight playing both, because a
   * second shadow map is the most expensive thing this feature could have
   * bought and the two are never in the sky together), the hemisphere fill
   * and the bounce.  main.js constructs one and calls update(dt) each
   * frame and follow(pos) from the camera update.
   *
   * @param renderer  for the PMREM generator
   * @param scene     background/environment/fog are installed on it
   * @param coarse    the phone question, for the shadow map size
   */
  constructor({ renderer, scene, coarse = false }) {
    this.scene = scene;

    // Sun/moon: one warm (or cool) key.  Shadow numbers are unchanged from
    // the fixed-sun build: 1024 on a phone -- the shadow camera follows the
    // player over a 26 m span, so 2048 buys 12.7 mm of texel and 1024 buys
    // 25.4; against a cat 0.32 m at the withers both resolve its shadow, and
    // the smaller map is a quarter of the depth pass and of the memory.
    const sun = new THREE.DirectionalLight(0xffe2b4, 2.5);
    sun.castShadow = true;
    sun.shadow.mapSize.set(coarse ? 1024 : 2048, coarse ? 1024 : 2048);
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 70;
    sun.shadow.bias = -0.0012;
    sun.shadow.normalBias = 0.02;
    // The shadow camera follows the player rather than covering the level:
    // at street scale a map big enough for everything would be far too
    // coarse to show a cat-sized shadow.
    const shadowSpan = 13;
    Object.assign(sun.shadow.camera,
      { left: -shadowSpan, right: shadowSpan, top: shadowSpan, bottom: -shadowSpan });
    sun.shadow.camera.updateProjectionMatrix();
    this.sun = sun;

    /**
     * The fill, shared with the environment map.  Both are ambient light
     * from the sky and they add: an environment at intensity 1 is worth a
     * hemisphere light of intensity PI, so switching it on and leaving this
     * at its old 1.55 lifted the whole street by roughly a stop.  The split
     * is 0.32 environment / 0.85 hemisphere, SOLVED rather than eyeballed
     * -- against the previous lighting it lands sky-facing irradiance at
     * 1.00x, a vertical wall at 1.00x and a downward face at 1.02x.  The
     * cycle never touches either number; see the header for how night
     * happens without bending this.  The hemisphere stays in the mix
     * because it is the only light that reads the LEVEL's ground colour:
     * its olive lower half is the lawn bounce under the eaves that a
     * gradient in the sky cannot know about -- by night the same slot is
     * the moonlit lawn, dimmed in step with the environment's ground band.
     */
    this.hemi = new THREE.HemisphereLight(0xc6dcf0, 0x74854f, 0.85);
    this.bounce = new THREE.DirectionalLight(0xdccdb0, 0.55);
    this.bounce.position.set(-5, 3, -6);
    scene.add(sun, sun.target, this.hemi, this.bounce);

    scene.fog = new THREE.Fog(0xc9d3d8, 38, 105); // sized for the street, not a clearing

    // The background: a 4-px equirect strip (width is plenty for a gradient
    // nothing samples laterally), repainted on the BG_STEP schedule.  The
    // canvas is reused; the TEXTURE around it is not -- _redrawBackground
    // says why a repaint-in-place background stays stale forever.
    this._bg = document.createElement("canvas");
    this._bg.width = 4; this._bg.height = 256;
    this._bgTex = null;

    // The environment source: 512 px wide, and the width is not cosmetic --
    // PMREMGenerator sizes the cube it prefilters as image.width / 4, so
    // the background's 4 px strip would make a ONE pixel cube face, no mip
    // chain, and an environment that measurably contributed nothing.  512
    // gives a 128 px face to filter, same as the fixed-sky build.
    this._envSrc = document.createElement("canvas");
    this._envSrc.width = 512; this._envSrc.height = 256;
    this._envTex = new THREE.CanvasTexture(this._envSrc);
    this._envTex.mapping = THREE.EquirectangularReflectionMapping;
    this._envTex.colorSpace = THREE.SRGBColorSpace;
    // One generator for the whole session, shader precompiled here: building
    // it per rebuild would recompile the prefilter program on the ENV_STEP
    // cadence, which is a hitch bought for nothing.
    this._pmrem = new THREE.PMREMGenerator(renderer);
    this._pmrem.compileEquirectangularShader();
    this._envRT = null;
    scene.environmentIntensity = 0.32;

    /**
     * The creek's authored reflection strength, captured once.  The water
     * material is tuned in materials.js (and is being retuned in a parallel
     * branch); this module's whole licence over it is a TIME-OF-DAY
     * multiplier on whatever number it shipped with -- never a value of its
     * own -- so the daytime creek is bit-identical to a build without this
     * file, and a retune on main flows through the night automatically.
     */
    this._waterBase = MATS.water.envMapIntensity;

    /** Where the key light sits relative to the player; follow() applies
        it.  Starts at the shipped offset and is re-solved every frame. */
    this._keyOffset = KEY_ANCHOR.clone();

    // The clock.  Local wall time through the shared formula until the
    // server's first word; see the header.
    this.phase = dayPhase(Date.now());
    this._err = 0;           // residual vs the server, eased away in update
    this._synced = false;    // a first word snaps; later words ease
    this._pin = null;        // preview() parks the sky here for the harness

    // Scratch for the per-frame mix -- allocated once, written every frame.
    this._mix = {
      sky: STOP_SKY.map(() => [0, 0, 0]),
      env: STOP_ENV.map(() => [0, 0, 0]),
      key: [0, 0, 0], keyI: 0,
      hemiSky: [0, 0, 0], hemiGround: [0, 0, 0],
      bounce: [0, 0, 0], fog: [0, 0, 0], water: 1,
    };
    /** Milliseconds the last environment rebuild cost; for the harness. */
    this.envCostMs = 0;

    // First paint, unconditionally: the scene must never render a frame
    // with an empty background or a missing environment.
    this._evalMix();
    this._applyFrame();
    this._redrawBackground();
    this._rebuildEnv();
  }

  /** The server's word on the phase.  First word (or a wild one) snaps;
      every later word leaves a residual for update() to ease out. */
  onServerPhase(ph) {
    if (typeof ph !== "number" || !Number.isFinite(ph)) return;
    if (this._pin !== null) return;   // a preview outranks the wire until released
    ph = ((ph % 1) + 1) % 1;
    const err = wrapSigned(ph - this.phase);
    if (!this._synced || Math.abs(err) > PHASE_SNAP) {
      this.phase = ph;
      this._err = 0;
      this._synced = true;
    } else {
      this._err = err;
    }
  }

  /**
   * Park the sky at a phase, for the test harness and for looking at dusk
   * without waiting twenty minutes: catGame.daycycle.preview(0.815).
   * preview(null) hands the clock back to the server.  Forces both coarse
   * rebuilds immediately so a screenshot taken on the next line is honest.
   */
  preview(ph) {
    this._pin = typeof ph === "number" ? ((ph % 1) + 1) % 1 : null;
    if (this._pin !== null) {
      this.phase = this._pin;
      this._err = 0;
      this._evalMix();
      this._applyFrame();
      this._redrawBackground();
      this._rebuildEnv();
    }
  }

  /** Advance the clock and move everything cheap; rebuild the coarse pieces
      only when the phase has crossed their step.  Called once per frame. */
  update(dt) {
    if (this._pin === null) {
      this.phase = (this.phase + dt / DAY_LENGTH_S) % 1;
      if (this._err !== 0) {
        const step = this._err * (1 - Math.exp(-dt / PHASE_TAU));
        this.phase = ((this.phase + step) % 1 + 1) % 1;
        this._err -= step;
        if (Math.abs(this._err) < 1e-7) this._err = 0;
      }
    }

    this._evalMix();
    this._applyFrame();
    if (Math.abs(wrapSigned(this.phase - this._bgPh)) >= BG_STEP) this._redrawBackground();
    if (Math.abs(wrapSigned(this.phase - this._envPh)) >= ENV_STEP) this._rebuildEnv();
  }

  /** Keep the key light on the player, at wherever the hour has put it in
      the sky.  Called from the camera update, replacing the fixed
      (+9, +14, +6) the pre-cycle build hung the sun at. */
  follow(pos) {
    this.sun.position.copy(pos).add(this._keyOffset);
    this.sun.target.position.copy(pos);
    this.sun.target.updateMatrixWorld();
  }

  // -- internals ------------------------------------------------------------

  /** Interpolate the keyframe table (cyclically) into this._mix. */
  _evalMix() {
    const ph = this.phase;
    let a = PARSED[PARSED.length - 1], b = PARSED[0];
    for (let i = 0; i < PARSED.length; i++) {
      if (PARSED[i].ph <= ph) { a = PARSED[i]; b = PARSED[(i + 1) % PARSED.length]; }
    }
    const span = ((b.ph - a.ph) % 1 + 1) % 1 || 1;
    const f = (((ph - a.ph) % 1 + 1) % 1) / span;

    const m = this._mix;
    for (let i = 0; i < m.sky.length; i++) lerp3(m.sky[i], a.sky[i], b.sky[i], f);
    for (let i = 0; i < m.env.length; i++) lerp3(m.env[i], a.env[i], b.env[i], f);
    lerp3(m.key, a.key, b.key, f);
    m.keyI = lerp(a.keyI, b.keyI, f);
    lerp3(m.hemiSky, a.hemiSky, b.hemiSky, f);
    lerp3(m.hemiGround, a.hemiGround, b.hemiGround, f);
    lerp3(m.bounce, a.bounce, b.bounce, f);
    lerp3(m.fog, a.fog, b.fog, f);
    m.water = lerp(a.water, b.water, f);
  }

  /** Everything cheap enough to move every frame: the key light's place,
      colour and strength, the fills, the fog, the creek's multiplier. */
  _applyFrame() {
    const m = this._mix;
    const ph = this.phase;

    // Whose sky is it -- the sun's road or the moon's?
    let el, az;
    if (ph >= SUNRISE && ph < SUNSET) {
      const u = (ph - SUNRISE) / (SUNSET - SUNRISE);
      el = SUN_EL_MAX * Math.sin(Math.PI * u);
      az = AZ0 + AZ_SWEEP * u;
    } else {
      const nightLen = 1 - (SUNSET - SUNRISE);
      const v = ((((ph - SUNSET) % 1) + 1) % 1) / nightLen;
      el = MOON_EL_MAX * Math.sin(Math.PI * v);
      az = AZ0 + AZ_SWEEP * v;
    }
    const fade = THREE.MathUtils.smoothstep(
      el, 0, THREE.MathUtils.degToRad(HORIZON_FADE_DEG));
    const elR = Math.max(el, EL_MIN);
    const ce = Math.cos(elR);
    this._keyOffset.set(Math.sin(az) * ce, Math.sin(elR), Math.cos(az) * ce)
      .multiplyScalar(KEY_DIST);

    setCol(this.sun.color, m.key);
    this.sun.intensity = m.keyI * fade;

    setCol(this.hemi.color, m.hemiSky);
    setCol(this.hemi.groundColor, m.hemiGround);
    setCol(this.bounce.color, m.bounce);
    setCol(this.scene.fog.color, m.fog);
    MATS.water.envMapIntensity = this._waterBase * m.water;
  }

  _drawStrip(canvas, stops, colours) {
    const g = canvas.getContext("2d");
    const grad = g.createLinearGradient(0, 0, 0, canvas.height);
    for (let i = 0; i < stops.length; i++) grad.addColorStop(stops[i], css(colours[i]));
    g.fillStyle = grad;
    g.fillRect(0, 0, canvas.width, canvas.height);
  }

  /**
   * A NEW CanvasTexture per repaint, not needsUpdate on a kept one, and the
   * difference was a sky stuck forever on its first frame: the renderer
   * does not draw an equirect background directly -- WebGLEnvironments
   * converts it to a cubemap ONCE and caches the conversion in a WeakMap
   * keyed on the texture OBJECT, with no eye on the texture's version.
   * needsUpdate re-uploads the source strip but the cached cube keeps
   * rendering, so the repaint is invisible; only dispose() (which the cache
   * listens for) evicts it.  A fresh texture per step gets a fresh
   * conversion, and disposing the old one frees its cube behind it.  The
   * conversion is 6 tiny renders into a 256-cube -- at the BG_STEP cadence,
   * noise.  (scene.environment does not have this trap: the PMREM rebuild
   * below hands over a brand-new render-target texture every time anyway.)
   */
  _redrawBackground() {
    this._bgPh = this.phase;
    this._drawStrip(this._bg, STOP_SKY, this._mix.sky);
    const old = this._bgTex;
    this._bgTex = new THREE.CanvasTexture(this._bg);
    this._bgTex.mapping = THREE.EquirectangularReflectionMapping;
    this._bgTex.colorSpace = THREE.SRGBColorSpace;
    this.scene.background = this._bgTex;
    if (old) old.dispose();
  }

  /** The expensive one -- ENV_STEP's comment carries the budget.  The old
      render target is disposed only after the scene points at the new one,
      so no frame ever samples a freed texture. */
  _rebuildEnv() {
    const t0 = performance.now();
    this._envPh = this.phase;
    this._drawStrip(this._envSrc, STOP_ENV, this._mix.env);
    this._envTex.needsUpdate = true;
    const rt = this._pmrem.fromEquirectangular(this._envTex);
    const old = this._envRT;
    this._envRT = rt;
    this.scene.environment = rt.texture;
    // The water carries its OWN copy of the environment (materials.js's
    // useEnvironment says why: the shared path would clamp its reflection
    // to the street's 0.32), so every rebuild has to be handed to it too.
    useEnvironment(rt.texture);
    if (old) old.dispose();
    this.envCostMs = performance.now() - t0;
  }
}
